import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useDate } from '../hooks/useDate';
import './Landing.css';

export function Landing() {
  const { t } = useTranslation();
  const { currentYear } = useDate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  
  // Track which card is focused - with debounce for smoother experience
  const [focusedCard, setFocusedCard] = useState<string>('d8');
  const [hoverTimeout, setHoverTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCardHover = useCallback((cardId: string) => {
    // Clear any pending timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Add a small delay before switching focus for smoother experience
    const timeout = setTimeout(() => {
      setFocusedCard(cardId);
    }, 150); // 150ms delay feels natural
    
    setHoverTimeout(timeout);
  }, [hoverTimeout]);

  const handleCarouselLeave = useCallback(() => {
    // When leaving the carousel area, smoothly return to center (d8)
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setFocusedCard('d8');
    }, 300);
    setHoverTimeout(timeout);
  }, [hoverTimeout]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage(t('waitlist.invalidEmail'));
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(t('waitlist.success'));
        setEmail('');
      } else if (response.status === 409) {
        // Duplicate email
        setStatus('error');
        setMessage(t('waitlist.alreadyRegistered'));
      } else if (response.status === 400) {
        // Validation error
        setStatus('error');
        setMessage(t('waitlist.invalidEmail'));
      } else {
        setStatus('error');
        setMessage(data.error?.message || t('common.error'));
      }
    } catch {
      setStatus('error');
      setMessage(t('common.error'));
    }
  };

  return (
    <div className="landing">
      {/* Top Banner */}
      <div className="top-banner">
        <div className="container">
          <span>🎓 {t('landing.banner')}</span>
        </div>
      </div>

      {/* Header */}
      <header className="landing-header">
        <div className="container flex justify-between items-center">
          <a href="/" className="logo">
            <img src="/logo-header.png" alt="SolarMaktab" className="logo-img" />
          </a>
          <nav className="landing-nav flex gap-lg items-center">
            <a href="#features" className="nav-link">{t('landing.features.title')}</a>
            <a href="#how-it-works" className="nav-link">{t('landing.howItWorks.title')}</a>
            <a href="/schools" className="nav-link">{t('schools.discover')}</a>
            <LanguageSwitcher />
            <a href="/login" className="btn btn-outline btn-sm">{t('common.login')}</a>
            <a href="#waitlist" className="btn btn-primary btn-sm">{t('landing.hero.cta')}</a>
          </nav>
          <div className="mobile-header-actions">
            <LanguageSwitcher />
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`} 
              aria-label="Menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <nav className="mobile-nav">
            <a href="#features" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>{t('landing.features.title')}</a>
            <a href="#tablets" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>{t('landing.tablets.badge')}</a>
            <a href="#how-it-works" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>{t('landing.howItWorks.title')}</a>
            <a href="/schools" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>{t('schools.discover')}</a>
            <div className="mobile-nav-divider"></div>
            <a href="/login" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>{t('common.login')}</a>
            <a href="#waitlist" className="btn btn-primary w-full" onClick={() => setMobileMenuOpen(false)}>{t('landing.hero.cta')}</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <span className="badge mb-md">{t('landing.hero.badge')}</span>
              <h1 className="hero-title display-heading">{t('landing.hero.title')}</h1>
              <p className="hero-subtitle">{t('landing.hero.subtitle')}</p>
              
              <div className="hero-stats">
                <div className="stat">
                  <span className="stat-number">10K+</span>
                  <span className="stat-label">{t('landing.hero.stats.students')}</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat">
                  <span className="stat-number">500+</span>
                  <span className="stat-label">{t('landing.hero.stats.lessons')}</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">{t('landing.hero.stats.offline')}</span>
                </div>
              </div>

              <form className="waitlist-form" onSubmit={handleWaitlistSubmit} id="waitlist">
                <div className="waitlist-input-group">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('waitlist.emailPlaceholder')}
                    disabled={status === 'loading'}
                  />
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg"
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? (
                      <span className="animate-pulse">{t('common.loading')}</span>
                    ) : (
                      t('landing.hero.cta')
                    )}
                  </button>
                </div>
                {message && (
                  <p className={`waitlist-message ${status}`}>{message}</p>
                )}
                <p className="waitlist-note">{t('waitlist.note')}</p>
              </form>
            </div>
            
            <div className="hero-visual">
              <div className="hero-image-wrapper">
                <img 
                  src="/hero-tablet.jpg" 
                  alt={t('landing.hero.tabletLabel')}
                  className="hero-image"
                  loading="eager"
                />
                <div className="hero-image-glow"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-bg-pattern"></div>
      </section>

      {/* Trusted By Section */}
      <section className="trusted-by">
        <div className="container">
          <p className="trusted-label">{t('landing.trustedBy')}</p>
          <div className="trusted-logos">
            <span className="trusted-logo">🏫 {t('landing.schools.school1')}</span>
            <span className="trusted-logo">🎓 {t('landing.schools.school2')}</span>
            <span className="trusted-logo">📚 {t('landing.schools.school3')}</span>
          </div>
        </div>
      </section>

      {/* Tablet Devices Section */}
      <section id="tablets" className="tablets-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge mb-md">{t('landing.tablets.badge')}</span>
            <h2>{t('landing.tablets.title')}</h2>
            <p className="section-subtitle">{t('landing.tablets.subtitle')}</p>
          </div>

          {/* Trust badges */}
          <div className="trust-badges">
            <span className="trust-badge">
              <span className="trust-icon">🚚</span>
              {t('landing.tablets.freeShipping')}
            </span>
            <span className="trust-badge">
              <span className="trust-icon">🛡️</span>
              {t('landing.tablets.warranty')}
            </span>
            <span className="trust-badge">
              <span className="trust-icon">⚡</span>
              {t('landing.tablets.limitedOffer')}
            </span>
          </div>
          
          <div className="tablet-cards-carousel" onMouseLeave={handleCarouselLeave}>
            {/* Danishcha 7 */}
            <div 
              className={`tablet-card tablet-7 ${focusedCard === 'd7' ? 'focused' : ''} ${focusedCard === 'd8' ? 'left' : ''} ${focusedCard === 'd10' ? 'far-left' : ''}`}
              onMouseEnter={() => handleCardHover('d7')}
              onClick={() => window.location.href = '/tablets/danishcha-7'}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (window.location.href = '/tablets/danishcha-7')}
            >
              <div className="card-inner">
                <div className="savings-badge">{t('landing.tablets.d7.savings')}</div>
                <div className="tablet-image-wrapper">
                  <div className="tablet-mockup">
                    <div className="tablet-screen">
                      <span className="screen-content">7"</span>
                    </div>
                  </div>
                </div>
                <h3 className="tablet-name">{t('landing.tablets.d7.name')}</h3>
                <p className="tablet-tagline">{t('landing.tablets.d7.tagline')}</p>
                <ul className="tablet-specs">
                  <li><span className="spec-icon">📱</span><span>{t('landing.tablets.d7.screen')}</span></li>
                  <li><span className="spec-icon">💾</span><span>{t('landing.tablets.d7.storage')}</span></li>
                  <li><span className="spec-icon">🧠</span><span>{t('landing.tablets.d7.ram')}</span></li>
                  <li><span className="spec-icon">🔋</span><span>{t('landing.tablets.d7.battery')}</span></li>
                </ul>
                <div className="tablet-price-wrapper">
                  <div className="tablet-price">{t('landing.tablets.d7.price')}</div>
                </div>
                <button className="tablet-btn">{t('landing.tablets.cta')}</button>
              </div>
            </div>

            {/* Danishcha 8 - Center/Featured */}
            <div 
              className={`tablet-card tablet-8 ${focusedCard === 'd8' ? 'focused' : ''} ${focusedCard === 'd7' ? 'right' : ''} ${focusedCard === 'd10' ? 'left' : ''}`}
              onMouseEnter={() => handleCardHover('d8')}
              onClick={() => window.location.href = '/tablets/danishcha-8'}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (window.location.href = '/tablets/danishcha-8')}
            >
              <div className="card-inner">
                <span className="featured-badge">{t('landing.tablets.popular')}</span>
                <div className="savings-badge">{t('landing.tablets.d8.savings')}</div>
                <div className="tablet-image-wrapper">
                  <div className="tablet-mockup">
                    <div className="tablet-screen">
                      <span className="screen-content">8"</span>
                    </div>
                  </div>
                </div>
                <h3 className="tablet-name">{t('landing.tablets.d8.name')}</h3>
                <p className="tablet-tagline">{t('landing.tablets.d8.tagline')}</p>
                <ul className="tablet-specs">
                  <li><span className="spec-icon">📱</span><span>{t('landing.tablets.d8.screen')}</span></li>
                  <li><span className="spec-icon">💾</span><span>{t('landing.tablets.d8.storage')}</span></li>
                  <li><span className="spec-icon">🧠</span><span>{t('landing.tablets.d8.ram')}</span></li>
                  <li><span className="spec-icon">🔋</span><span>{t('landing.tablets.d8.battery')}</span></li>
                </ul>
                <div className="tablet-price-wrapper">
                  <div className="tablet-price">{t('landing.tablets.d8.price')}</div>
                </div>
                <button className="tablet-btn">{t('landing.tablets.cta')}</button>
              </div>
            </div>

            {/* Danishcha 10 */}
            <div 
              className={`tablet-card tablet-10 ${focusedCard === 'd10' ? 'focused' : ''} ${focusedCard === 'd8' ? 'right' : ''} ${focusedCard === 'd7' ? 'far-right' : ''}`}
              onMouseEnter={() => handleCardHover('d10')}
              onClick={() => window.location.href = '/tablets/danishcha-10'}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && (window.location.href = '/tablets/danishcha-10')}
            >
              <div className="card-inner">
                <div className="savings-badge">{t('landing.tablets.d10.savings')}</div>
                <div className="tablet-image-wrapper">
                  <div className="tablet-mockup">
                    <div className="tablet-screen">
                      <span className="screen-content">10"</span>
                    </div>
                  </div>
                </div>
                <h3 className="tablet-name">{t('landing.tablets.d10.name')}</h3>
                <p className="tablet-tagline">{t('landing.tablets.d10.tagline')}</p>
                <ul className="tablet-specs">
                  <li><span className="spec-icon">📱</span><span>{t('landing.tablets.d10.screen')}</span></li>
                  <li><span className="spec-icon">💾</span><span>{t('landing.tablets.d10.storage')}</span></li>
                  <li><span className="spec-icon">🧠</span><span>{t('landing.tablets.d10.ram')}</span></li>
                  <li><span className="spec-icon">🔋</span><span>{t('landing.tablets.d10.battery')}</span></li>
                </ul>
                <div className="tablet-price-wrapper">
                  <div className="tablet-price">{t('landing.tablets.d10.price')}</div>
                </div>
                <button className="tablet-btn">{t('landing.tablets.cta')}</button>
              </div>
            </div>
          </div>

          {/* Compare link */}
          <div className="compare-section">
            <a href="#compare" className="compare-link">
              {t('landing.tablets.compare')} →
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge mb-md">{t('landing.features.badge')}</span>
            <h2>{t('landing.features.title')}</h2>
            <p className="section-subtitle">{t('landing.features.subtitle')}</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <span className="feature-icon">📴</span>
              </div>
              <h3>{t('landing.features.offline.title')}</h3>
              <p>{t('landing.features.offline.description')}</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <span className="feature-icon">🔄</span>
              </div>
              <h3>{t('landing.features.sync.title')}</h3>
              <p>{t('landing.features.sync.description')}</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <span className="feature-icon">🌍</span>
              </div>
              <h3>{t('landing.features.persian.title')}</h3>
              <p>{t('landing.features.persian.description')}</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-wrapper">
                <span className="feature-icon">💰</span>
              </div>
              <h3>{t('landing.features.affordable.title')}</h3>
              <p>{t('landing.features.affordable.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge mb-md">{t('landing.howItWorks.badge')}</span>
            <h2>{t('landing.howItWorks.title')}</h2>
            <p className="section-subtitle">{t('landing.howItWorks.subtitle')}</p>
          </div>
          
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>{t('landing.howItWorks.step1.title')}</h3>
              <p>{t('landing.howItWorks.step1.description')}</p>
            </div>
            
            <div className="step-connector">→</div>
            
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>{t('landing.howItWorks.step2.title')}</h3>
              <p>{t('landing.howItWorks.step2.description')}</p>
            </div>
            
            <div className="step-connector">→</div>
            
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>{t('landing.howItWorks.step3.title')}</h3>
              <p>{t('landing.howItWorks.step3.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Discover Schools Section */}
      <section id="discover-schools" className="discover-schools-section">
        <div className="container">
          <div className="discover-schools-content">
            <div className="discover-schools-text">
              <span className="badge mb-md">{t('discoverSchools.badge')}</span>
              <h2>{t('discoverSchools.title')}</h2>
              <p className="section-subtitle">{t('discoverSchools.subtitle')}</p>
              
              <div className="discover-features">
                <div className="discover-feature">
                  <span className="discover-feature-icon">{t('discoverSchools.feature1.icon')}</span>
                  <div>
                    <h4>{t('discoverSchools.feature1.title')}</h4>
                    <p>{t('discoverSchools.feature1.description')}</p>
                  </div>
                </div>
                <div className="discover-feature">
                  <span className="discover-feature-icon">{t('discoverSchools.feature2.icon')}</span>
                  <div>
                    <h4>{t('discoverSchools.feature2.title')}</h4>
                    <p>{t('discoverSchools.feature2.description')}</p>
                  </div>
                </div>
                <div className="discover-feature">
                  <span className="discover-feature-icon">{t('discoverSchools.feature3.icon')}</span>
                  <div>
                    <h4>{t('discoverSchools.feature3.title')}</h4>
                    <p>{t('discoverSchools.feature3.description')}</p>
                  </div>
                </div>
              </div>
              
              <div className="discover-actions">
                <a href="/schools" className="btn btn-primary btn-lg">{t('discoverSchools.cta')}</a>
                <a href="/login" className="btn btn-outline btn-lg">{t('discoverSchools.loginCta')}</a>
              </div>
            </div>
            
            <div className="discover-schools-visual">
              <div className="schools-preview">
                <div className="school-card-preview">
                  <div className="school-icon">🏫</div>
                  <div className="school-info">
                    <span className="school-name">Kabul Academy</span>
                    <span className="school-stats">250+ students</span>
                  </div>
                </div>
                <div className="school-card-preview">
                  <div className="school-icon">📚</div>
                  <div className="school-info">
                    <span className="school-name">Herat Learning</span>
                    <span className="school-stats">180+ students</span>
                  </div>
                </div>
                <div className="school-card-preview">
                  <div className="school-icon">🎓</div>
                  <div className="school-info">
                    <span className="school-name">Rural Education</span>
                    <span className="school-stats">320+ students</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="container">
          <div className="section-header text-center">
            <span className="badge mb-md">{t('landing.pricing.badge')}</span>
            <h2>{t('landing.pricing.title')}</h2>
            <p className="section-subtitle">{t('landing.pricing.subtitle')}</p>
          </div>
          
          <div className="pricing-card">
            <div className="pricing-coming-soon">
              <span className="coming-soon-icon">🚀</span>
              <h3>{t('landing.pricing.comingSoon')}</h3>
              <p>{t('landing.pricing.comingSoonDesc')}</p>
              <a href="#waitlist" className="btn btn-outline">{t('landing.pricing.notifyMe')}</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>{t('landing.cta.title')}</h2>
            <p>{t('landing.cta.subtitle')}</p>
            <a href="#waitlist" className="btn btn-primary btn-lg">{t('landing.hero.cta')}</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <a href="/" className="logo">
                <img src="/logo-header.png" alt="SolarMaktab" className="logo-img" />
              </a>
              <p className="footer-tagline">{t('footer.tagline')}</p>
            </div>
            
            <div className="footer-links-grid">
              <div className="footer-column">
                <h4>{t('footer.product')}</h4>
                <a href="#features">{t('landing.features.title')}</a>
                <a href="#pricing">{t('landing.pricing.title')}</a>
                <a href="#how-it-works">{t('landing.howItWorks.title')}</a>
                <a href="/schools">{t('schools.discover')}</a>
              </div>
              <div className="footer-column">
                <h4>{t('footer.company')}</h4>
                <a href="/about">{t('footer.about')}</a>
                <a href="/contact">{t('footer.contact')}</a>
              </div>
              <div className="footer-column">
                <h4>{t('footer.legal')}</h4>
                <a href="/privacy">{t('footer.privacy')}</a>
                <a href="/terms">{t('footer.terms')}</a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>{t('footer.copyright', { year: currentYear })}</p>
            <div className="footer-social">
              <a href="#" aria-label="Twitter">𝕏</a>
              <a href="#" aria-label="Instagram">📷</a>
              <a href="#" aria-label="Telegram">✈️</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
